import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useAppConfigurationSelector } from "../../redux/reducers/AppConfigurationReducer/AppConfigurationReducer";
import { useInfosSelector } from "../../redux/reducers/infos/infosReducer";
import { useModalSelector } from "../../redux/reducers/ModalReducer/ModalReducer";
import { useMsTeamsSelector } from "../../redux/reducers/MicrosoftTeamsReducer/MicrosoftTeamsReducer";
/** Seen once per user, on this device: the tour is a first-run aid, the settings menu brings it back. */
export const onboardingTourStorageKey = (appKey, userId) => `${appKey}.onboardingTour.v1.${userId}`;
/** Leaves the view time to settle, and the boot dialogs (privacy consent) time to open first. */
export const ONBOARDING_TOUR_AUTO_LAUNCH_DELAY_MS = 800;
const SPOTLIGHT_PADDING = 6;
const SPOTLIGHT_RADIUS = 12;
const CARD_GAP = 14;
const VIEWPORT_MARGIN = 16;
const hasSeenTour = (key) => {
    try {
        return window.localStorage.getItem(key) === "1";
    }
    catch {
        // Storage blocked: better never auto-launch than show the tour on every load.
        return true;
    }
};
const markTourSeen = (key) => {
    try {
        window.localStorage.setItem(key, "1");
    }
    catch {
        // Storage blocked: already treated as seen by hasSeenTour.
    }
};
const findTargets = (testIds) => testIds
    .flatMap((id) => Array.from(document.querySelectorAll(`[data-testid="${id}"]`)))
    .filter((el) => {
    const rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
});
/** The padded bounding union of the step's targets, clamped to the viewport, or null when none is on screen. */
const measureSpotlight = (testIds, viewport) => {
    const rects = findTargets(testIds).map((el) => el.getBoundingClientRect());
    if (rects.length === 0)
        return null;
    const top = Math.max(Math.min(...rects.map((r) => r.top)) - SPOTLIGHT_PADDING, 4);
    const left = Math.max(Math.min(...rects.map((r) => r.left)) - SPOTLIGHT_PADDING, 4);
    const bottom = Math.min(Math.max(...rects.map((r) => r.bottom)) + SPOTLIGHT_PADDING, viewport.height - 4);
    const right = Math.min(Math.max(...rects.map((r) => r.right)) + SPOTLIGHT_PADDING, viewport.width - 4);
    if (bottom <= top || right <= left)
        return null;
    return { top, left, width: right - left, height: bottom - top };
};
const round = (n) => Math.round(n * 10) / 10;
/**
 * The blur layer covers the whole screen except a rounded hole over the spotlight, which is what
 * keeps that zone sharp. Without a spotlight the hole shrinks to a point at the centre: the path
 * keeps the same shape, so the browser animates the hole from one step to the next.
 */
export const spotlightClipPath = (viewport, rect) => {
    const zone = rect ?? { top: viewport.height / 2, left: viewport.width / 2, width: 0, height: 0 };
    const X = round(zone.left);
    const Y = round(zone.top);
    const W = round(zone.width);
    const H = round(zone.height);
    const R = round(Math.min(SPOTLIGHT_RADIUS, zone.width / 2, zone.height / 2));
    const outer = `M0 0H${round(viewport.width)}V${round(viewport.height)}H0Z`;
    const hole = `M${X + R} ${Y}H${X + W - R}A${R} ${R} 0 0 1 ${X + W} ${Y + R}V${Y + H - R}` +
        `A${R} ${R} 0 0 1 ${X + W - R} ${Y + H}H${X + R}A${R} ${R} 0 0 1 ${X} ${Y + H - R}V${Y + R}A${R} ${R} 0 0 1 ${X + R} ${Y}Z`;
    return `path(evenodd, "${outer} ${hole}")`;
};
/**
 * Next to the spotlight: below it when there is room, above it otherwise, and docked at the bottom
 * of the screen when the zone is too tall for either (a grid, a list). Undefined means centred.
 */
export const placeCard = (rect, card, viewport) => {
    if (!rect)
        return undefined;
    const maxTop = Math.max(viewport.height - card.height - VIEWPORT_MARGIN, VIEWPORT_MARGIN);
    const below = rect.top + rect.height + CARD_GAP;
    const above = rect.top - CARD_GAP - card.height;
    const top = below + card.height <= viewport.height - VIEWPORT_MARGIN ? below : above >= VIEWPORT_MARGIN ? above : maxTop;
    const centred = rect.left + rect.width / 2 - card.width / 2;
    const left = Math.min(Math.max(centred, VIEWPORT_MARGIN), Math.max(viewport.width - card.width - VIEWPORT_MARGIN, VIEWPORT_MARGIN));
    return { top, left };
};
const readViewport = () => ({ width: window.innerWidth, height: window.innerHeight });
/**
 * An app's guided tour: opens by itself once per user, the first time the main view is ready with
 * no dialog on screen, and again on demand from the settings menu (`requestOnboardingTour`).
 */
export const useOnboardingTour = (props) => {
    const { onboardingTourRequestId } = useAppConfigurationSelector("onboardingTourRequestId");
    const { isOpen: openModal } = useModalSelector("isOpen");
    const { userId, tenantId, themeClass } = useMsTeamsSelector("userId", "tenantId", "themeClass");
    const { appInsightInstance } = useInfosSelector("appInsightInstance");
    const enabled = props.enabled ?? true;
    const isModalOpen = !!openModal;
    const storageKey = userId ? onboardingTourStorageKey(props.appKey, userId) : "";
    const [isOpen, setIsOpen] = useState(false);
    const [steps, setSteps] = useState([]);
    const [index, setIndex] = useState(0);
    const [canUseCta, setCanUseCta] = useState(false);
    const [viewport, setViewport] = useState(readViewport);
    const [rect, setRect] = useState(null);
    const [cardSize, setCardSize] = useState({ width: 360, height: 320 });
    const cardRef = useRef(null);
    const primaryButtonRef = useRef(null);
    const autoLaunched = useRef(false);
    // The request id lives in redux and survives the view being remounted (e.g. back from another
    // route): only a request made after mount replays the tour.
    const handledRequestId = useRef(onboardingTourRequestId);
    const step = steps[index];
    const ctaTarget = props.cta?.target;
    const track = useCallback((name, extra = {}) => {
        appInsightInstance?.trackEvent?.({ name, properties: { tenantId, app: props.appKey, ...extra } });
    }, [appInsightInstance, tenantId, props.appKey]);
    const start = useCallback((trigger) => {
        const available = props.steps.filter((s) => s.standalone || findTargets(s.targets).length > 0);
        const ctaButton = ctaTarget ? findTargets([ctaTarget])[0] : undefined;
        setCanUseCta(!!ctaButton && !ctaButton.disabled && ctaButton.getAttribute("aria-disabled") !== "true");
        setSteps(available);
        setIndex(0);
        setViewport(readViewport());
        setIsOpen(true);
        track("onboarding_tour_started", { trigger, steps: String(available.length) });
    }, [props.steps, ctaTarget, track]);
    // First run: once the view is on screen and no dialog (privacy consent, premium) is open.
    // A dialog opening meanwhile clears the timer; the tour then waits for it to close.
    useEffect(() => {
        if (!enabled || !props.isReady || !storageKey || isModalOpen || isOpen || autoLaunched.current)
            return;
        if (hasSeenTour(storageKey))
            return;
        const timer = setTimeout(() => {
            autoLaunched.current = true;
            start("auto");
        }, ONBOARDING_TOUR_AUTO_LAUNCH_DELAY_MS);
        return () => clearTimeout(timer);
    }, [enabled, props.isReady, storageKey, isModalOpen, isOpen, start]);
    // Replay from the settings menu.
    useEffect(() => {
        if (onboardingTourRequestId === handledRequestId.current)
            return;
        handledRequestId.current = onboardingTourRequestId;
        if (enabled && props.isReady)
            start("menu");
    }, [onboardingTourRequestId, enabled, props.isReady, start]);
    const close = useCallback((completed) => {
        setIsOpen(false);
        if (storageKey)
            markTourSeen(storageKey);
        track(completed ? "onboarding_tour_completed" : "onboarding_tour_dismissed", { step: step?.id ?? "", stepIndex: String(index) });
    }, [storageKey, track, step?.id, index]);
    const isLast = index === steps.length - 1;
    const next = useCallback(() => (isLast ? close(true) : setIndex((i) => i + 1)), [isLast, close]);
    const back = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);
    const dismiss = useCallback(() => close(false), [close]);
    const onCtaClick = props.cta?.onClick;
    const runCta = useCallback(() => {
        track("onboarding_tour_cta_clicked", { step: step?.id ?? "" });
        close(true);
        onCtaClick?.();
    }, [track, step?.id, close, onCtaClick]);
    // Follow the targets: the step changes, the window resizes or scrolls, the view reflows.
    useEffect(() => {
        if (!isOpen || !step)
            return;
        const first = findTargets(step.targets)[0];
        first?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
        let frame = 0;
        const update = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => {
                const current = readViewport();
                setViewport(current);
                setRect(measureSpotlight(step.targets, current));
            });
        };
        update();
        window.addEventListener("resize", update);
        window.addEventListener("scroll", update, true);
        const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : undefined;
        findTargets(step.targets).forEach((el) => observer?.observe(el));
        return () => {
            cancelAnimationFrame(frame);
            window.removeEventListener("resize", update);
            window.removeEventListener("scroll", update, true);
            observer?.disconnect();
        };
    }, [isOpen, step]);
    // The card is placed from its real size, which changes with the language and the step's text.
    useLayoutEffect(() => {
        const card = cardRef.current;
        if (!card)
            return;
        const { offsetWidth: width, offsetHeight: height } = card;
        if (width && height && (width !== cardSize.width || height !== cardSize.height))
            setCardSize({ width, height });
    });
    useEffect(() => {
        if (isOpen)
            primaryButtonRef.current?.focus();
    }, [isOpen, index]);
    // Keyboard: Esc closes, arrows navigate. Captured so that Esc does not also reach the page behind.
    useEffect(() => {
        if (!isOpen || isModalOpen)
            return;
        const onKey = (e) => {
            if (e.key === "Escape") {
                e.stopPropagation();
                dismiss();
            }
            else if (e.key === "ArrowRight")
                next();
            else if (e.key === "ArrowLeft")
                back();
        };
        window.addEventListener("keydown", onKey, true);
        return () => window.removeEventListener("keydown", onKey, true);
    }, [isOpen, isModalOpen, dismiss, next, back]);
    // Only a step's own accent is set inline: the brand accent comes from the stylesheet, which
    // adapts it to the dark and high-contrast themes (an inline value would override them).
    const accent = step?.accent;
    const placement = useMemo(() => placeCard(rect, cardSize, viewport), [rect, cardSize, viewport]);
    const backdropStyle = useMemo(() => ({ clipPath: spotlightClipPath(viewport, rect) }), [viewport, rect]);
    const haloStyle = useMemo(() => (rect ? { top: rect.top, left: rect.left, width: rect.width, height: rect.height } : undefined), [rect]);
    const cardStyle = useMemo(() => ({ ...(accent ? { ["--tp-tour-accent"]: accent } : {}), ...(placement ?? {}) }), [accent, placement]);
    return {
        // A dialog opened over the tour (e.g. from the menu behind it) takes precedence; the tour resumes after.
        visible: enabled && isOpen && !isModalOpen && !!step,
        step,
        index,
        total: steps.length,
        isFirst: index === 0,
        isLast,
        showCta: !!step?.showsCta && !!props.cta && canUseCta,
        hasSpotlight: !!rect,
        isPlaced: !!placement,
        themeClass,
        backdropStyle,
        haloStyle,
        cardStyle,
        cardRef,
        primaryButtonRef,
        next,
        back,
        dismiss,
        runCta,
    };
};
